import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Pagination,
  Snackbar,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  Description as DescriptionIcon,
  LocationOn as LocationIcon,
  ReportProblem as DisputeIcon,
  Dashboard as DashboardIcon,
  AttachMoney as MoneyIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const statusColors = {
  SENT: { bg: '#dbeafe', color: '#1d4ed8' },
  PAID: { bg: '#dcfce7', color: '#16a34a' },
  OVERDUE: { bg: '#fee2e2', color: '#dc2626' },
  PARTIAL: { bg: '#fef9c3', color: '#ca8a04' },
  DRAFT: { bg: '#f3f4f6', color: '#6b7280' },
  VOID: { bg: '#f3f4f6', color: '#6b7280' },
};

const disputeStatusColors = {
  OPEN: { bg: '#fef9c3', color: '#ca8a04' },
  UNDER_REVIEW: { bg: '#dbeafe', color: '#1d4ed8' },
  RESOLVED: { bg: '#dcfce7', color: '#16a34a' },
  REJECTED: { bg: '#fee2e2', color: '#dc2626' },
};

const statementTypeColors = {
  MONTHLY: { bg: '#dbeafe', color: '#1d4ed8' },
  QUARTERLY: { bg: '#f3e8ff', color: '#7c3aed' },
  ANNUAL: { bg: '#fef9c3', color: '#ca8a04' },
  CUSTOM: { bg: '#f3f4f6', color: '#6b7280' },
};

function StatusChip({ status, colorMap }) {
  const map = colorMap || statusColors;
  const s = map[status] || { bg: '#f3f4f6', color: '#6b7280' };
  return (
    <Chip
      label={status?.replace(/_/g, ' ')}
      size="small"
      sx={{
        fontFamily,
        fontWeight: 600,
        fontSize: '0.75rem',
        backgroundColor: s.bg,
        color: s.color,
        borderRadius: '6px',
      }}
    />
  );
}

function formatCurrency(amount) {
  if (amount == null) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const sxCard = {
  borderRadius: '12px',
  border: '1px solid #e5e7eb',
  boxShadow: 'none',
  '&:hover': { boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
};

const sxTableHead = {
  backgroundColor: '#f9fafb',
  '& th': {
    fontFamily,
    fontWeight: 600,
    fontSize: '0.75rem',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #e5e7eb',
    padding: '12px 16px',
  },
};

const sxTableCell = {
  fontFamily,
  fontSize: '0.875rem',
  color: '#374151',
  borderBottom: '1px solid #f3f4f6',
  padding: '12px 16px',
};

// ─────────────────────────── OVERVIEW TAB ───────────────────────────
function OverviewTab({ portalData, portalToken }) {
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecent = async () => {
      setLoading(true);
      try {
        const [invRes, payRes] = await Promise.all([
          axiosInstance.get(`/api/finance/portal/${portalToken}/invoices?page=1&per_page=5`),
          axiosInstance.get(`/api/finance/portal/${portalToken}/payments?page=1&per_page=5`),
        ]);
        setRecentInvoices(invRes.data?.data?.invoices || invRes.data?.invoices || []);
        setRecentPayments(payRes.data?.data?.payments || payRes.data?.payments || []);
      } catch (err) {
        console.error('Failed to fetch overview data', err);
      } finally {
        setLoading(false);
      }
    };
    fetchRecent();
  }, [portalToken]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress sx={{ color: ACCENT }} />
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={sxCard}>
            <CardContent sx={{ p: 3 }}>
              <Typography sx={{ fontFamily, fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>
                Customer
              </Typography>
              <Typography sx={{ fontFamily, fontSize: '1.25rem', fontWeight: 700, color: '#111827', mb: 0.5 }}>
                {portalData.display_name || portalData.customer_name || '-'}
              </Typography>
              <Typography sx={{ fontFamily, fontSize: '0.875rem', color: '#6b7280' }}>
                {portalData.email || ''}
              </Typography>
              {portalData.phone && (
                <Typography sx={{ fontFamily, fontSize: '0.875rem', color: '#6b7280', mt: 0.5 }}>
                  {portalData.phone}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ ...sxCard, borderLeft: `3px solid ${ACCENT}` }}>
            <CardContent sx={{ p: 3 }}>
              <Typography sx={{ fontFamily, fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>
                Outstanding Balance
              </Typography>
              <Typography sx={{ fontFamily, fontSize: '2rem', fontWeight: 700, color: '#111827' }}>
                {formatCurrency(portalData.outstanding_balance)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={sxCard}>
            <CardContent sx={{ p: 3 }}>
              <Typography sx={{ fontFamily, fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>
                Account Since
              </Typography>
              <Typography sx={{ fontFamily, fontSize: '1.25rem', fontWeight: 700, color: '#111827' }}>
                {formatDate(portalData.created_at)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Typography sx={{ fontFamily, fontSize: '1rem', fontWeight: 700, color: '#111827', mb: 2 }}>
        Recent Invoices
      </Typography>
      <TableContainer component={Paper} sx={{ ...sxCard, mb: 4 }}>
        <Table>
          <TableHead sx={sxTableHead}>
            <TableRow>
              <TableCell>Invoice #</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Balance Due</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {recentInvoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} sx={{ ...sxTableCell, textAlign: 'center', color: '#9ca3af', py: 4 }}>
                  No invoices found
                </TableCell>
              </TableRow>
            ) : (
              recentInvoices.map((inv) => (
                <TableRow key={inv.id} sx={{ '&:hover': { backgroundColor: '#f9fafb' } }}>
                  <TableCell sx={{ ...sxTableCell, fontWeight: 600 }}>{inv.invoice_number || inv.id}</TableCell>
                  <TableCell sx={sxTableCell}>{formatDate(inv.invoice_date || inv.created_at)}</TableCell>
                  <TableCell sx={sxTableCell}>{formatCurrency(inv.total)}</TableCell>
                  <TableCell sx={{ ...sxTableCell, fontWeight: 600 }}>{formatCurrency(inv.balance_due)}</TableCell>
                  <TableCell sx={sxTableCell}>
                    <StatusChip status={inv.status} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography sx={{ fontFamily, fontSize: '1rem', fontWeight: 700, color: '#111827', mb: 2 }}>
        Recent Payments
      </Typography>
      <TableContainer component={Paper} sx={sxCard}>
        <Table>
          <TableHead sx={sxTableHead}>
            <TableRow>
              <TableCell>Payment #</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Method</TableCell>
              <TableCell>Invoice #</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {recentPayments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} sx={{ ...sxTableCell, textAlign: 'center', color: '#9ca3af', py: 4 }}>
                  No payments found
                </TableCell>
              </TableRow>
            ) : (
              recentPayments.map((pay) => (
                <TableRow key={pay.id} sx={{ '&:hover': { backgroundColor: '#f9fafb' } }}>
                  <TableCell sx={{ ...sxTableCell, fontWeight: 600 }}>{pay.payment_number || pay.id}</TableCell>
                  <TableCell sx={sxTableCell}>{formatDate(pay.payment_date || pay.created_at)}</TableCell>
                  <TableCell sx={{ ...sxTableCell, fontWeight: 600, color: ACCENT }}>{formatCurrency(pay.amount)}</TableCell>
                  <TableCell sx={sxTableCell}>{pay.payment_method || '-'}</TableCell>
                  <TableCell sx={sxTableCell}>{pay.invoice_number || pay.invoice_id || '-'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ─────────────────────────── INVOICES TAB ───────────────────────────
function InvoicesTab({ portalToken }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('ALL');

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), per_page: '10' });
      if (statusFilter !== 'ALL') params.append('status', statusFilter);
      const res = await axiosInstance.get(`/api/finance/portal/${portalToken}/invoices?${params.toString()}`);
      const data = res.data?.data || res.data || {};
      setInvoices(data.invoices || []);
      setTotalPages(data.total_pages || 1);
    } catch (err) {
      console.error('Failed to fetch invoices', err);
    } finally {
      setLoading(false);
    }
  }, [portalToken, page, statusFilter]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleStatusChange = (_, newVal) => {
    setStatusFilter(newVal);
    setPage(1);
  };

  return (
    <Box>
      <Tabs
        value={statusFilter}
        onChange={handleStatusChange}
        sx={{
          mb: 3,
          '& .MuiTab-root': { fontFamily, fontWeight: 600, fontSize: '0.875rem', textTransform: 'none', minHeight: 40 },
          '& .Mui-selected': { color: ACCENT },
          '& .MuiTabs-indicator': { backgroundColor: ACCENT },
        }}
      >
        <Tab label="All" value="ALL" />
        <Tab label="Sent" value="SENT" />
        <Tab label="Paid" value="PAID" />
        <Tab label="Overdue" value="OVERDUE" />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: ACCENT }} />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} sx={sxCard}>
            <Table>
              <TableHead sx={sxTableHead}>
                <TableRow>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Balance Due</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ ...sxTableCell, textAlign: 'center', color: '#9ca3af', py: 4 }}>
                      No invoices found
                    </TableCell>
                  </TableRow>
                ) : (
                  invoices.map((inv) => (
                    <TableRow key={inv.id} sx={{ '&:hover': { backgroundColor: '#f9fafb' } }}>
                      <TableCell sx={{ ...sxTableCell, fontWeight: 600 }}>{inv.invoice_number || inv.id}</TableCell>
                      <TableCell sx={sxTableCell}>{formatDate(inv.invoice_date || inv.created_at)}</TableCell>
                      <TableCell sx={sxTableCell}>{formatDate(inv.due_date)}</TableCell>
                      <TableCell sx={sxTableCell}>{formatCurrency(inv.total)}</TableCell>
                      <TableCell sx={{ ...sxTableCell, fontWeight: 600 }}>{formatCurrency(inv.balance_due)}</TableCell>
                      <TableCell sx={sxTableCell}>
                        <StatusChip status={inv.status} />
                      </TableCell>
                      <TableCell sx={sxTableCell} align="right">
                        {inv.status !== 'PAID' && inv.status !== 'VOID' && inv.status !== 'DRAFT' && inv.balance_due > 0 && inv.payment_link && (
                          <Button
                            href={inv.payment_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                            variant="contained"
                            sx={{
                              fontFamily,
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              textTransform: 'none',
                              backgroundColor: ACCENT,
                              borderRadius: '8px',
                              boxShadow: 'none',
                              '&:hover': { backgroundColor: '#16a34a', boxShadow: 'none' },
                            }}
                          >
                            Pay Now
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, val) => setPage(val)}
                sx={{
                  '& .Mui-selected': { backgroundColor: `${ACCENT} !important`, color: '#fff' },
                }}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

// ─────────────────────────── PAYMENTS TAB ───────────────────────────
function PaymentsTab({ portalToken }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/api/finance/portal/${portalToken}/payments?page=${page}&per_page=10`);
      const data = res.data?.data || res.data || {};
      setPayments(data.payments || []);
      setTotalPages(data.total_pages || 1);
    } catch (err) {
      console.error('Failed to fetch payments', err);
    } finally {
      setLoading(false);
    }
  }, [portalToken, page]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress sx={{ color: ACCENT }} />
      </Box>
    );
  }

  return (
    <Box>
      <TableContainer component={Paper} sx={sxCard}>
        <Table>
          <TableHead sx={sxTableHead}>
            <TableRow>
              <TableCell>Payment #</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Method</TableCell>
              <TableCell>Invoice #</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} sx={{ ...sxTableCell, textAlign: 'center', color: '#9ca3af', py: 4 }}>
                  No payments found
                </TableCell>
              </TableRow>
            ) : (
              payments.map((pay) => (
                <TableRow key={pay.id} sx={{ '&:hover': { backgroundColor: '#f9fafb' } }}>
                  <TableCell sx={{ ...sxTableCell, fontWeight: 600 }}>{pay.payment_number || pay.id}</TableCell>
                  <TableCell sx={sxTableCell}>{formatDate(pay.payment_date || pay.created_at)}</TableCell>
                  <TableCell sx={{ ...sxTableCell, fontWeight: 600, color: ACCENT }}>{formatCurrency(pay.amount)}</TableCell>
                  <TableCell sx={sxTableCell}>{pay.payment_method || '-'}</TableCell>
                  <TableCell sx={sxTableCell}>{pay.invoice_number || pay.invoice_id || '-'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, val) => setPage(val)}
            sx={{
              '& .Mui-selected': { backgroundColor: `${ACCENT} !important`, color: '#fff' },
            }}
          />
        </Box>
      )}
    </Box>
  );
}

// ─────────────────────────── STATEMENTS TAB ───────────────────────────
function StatementsTab({ portalToken }) {
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStatements = async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.get(`/api/finance/portal/${portalToken}/statements`);
        setStatements(res.data?.data?.statements || res.data?.statements || []);
      } catch (err) {
        console.error('Failed to fetch statements', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStatements();
  }, [portalToken]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress sx={{ color: ACCENT }} />
      </Box>
    );
  }

  return (
    <Box>
      <TableContainer component={Paper} sx={sxCard}>
        <Table>
          <TableHead sx={sxTableHead}>
            <TableRow>
              <TableCell>Statement #</TableCell>
              <TableCell>Period</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Closing Balance</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {statements.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} sx={{ ...sxTableCell, textAlign: 'center', color: '#9ca3af', py: 4 }}>
                  No statements found
                </TableCell>
              </TableRow>
            ) : (
              statements.map((st) => (
                <TableRow key={st.id} sx={{ '&:hover': { backgroundColor: '#f9fafb' } }}>
                  <TableCell sx={{ ...sxTableCell, fontWeight: 600 }}>{st.statement_number || st.id}</TableCell>
                  <TableCell sx={sxTableCell}>
                    {st.period || `${formatDate(st.start_date)} - ${formatDate(st.end_date)}`}
                  </TableCell>
                  <TableCell sx={sxTableCell}>
                    <StatusChip status={st.type || st.statement_type} colorMap={statementTypeColors} />
                  </TableCell>
                  <TableCell sx={{ ...sxTableCell, fontWeight: 600 }}>{formatCurrency(st.closing_balance)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ─────────────────────────── BILLING INFO TAB ───────────────────────────
function BillingInfoTab({ portalToken, portalData, onPortalDataUpdate }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [form, setForm] = useState({
    billing_address_line1: '',
    billing_address_line2: '',
    billing_city: '',
    billing_state: '',
    billing_zip: '',
    billing_country: '',
    shipping_address_line1: '',
    shipping_address_line2: '',
    shipping_city: '',
    shipping_state: '',
    shipping_zip: '',
    shipping_country: '',
    phone: '',
  });

  useEffect(() => {
    if (portalData) {
      setForm({
        billing_address_line1: portalData.billing_address_line1 || '',
        billing_address_line2: portalData.billing_address_line2 || '',
        billing_city: portalData.billing_city || '',
        billing_state: portalData.billing_state || '',
        billing_zip: portalData.billing_zip || '',
        billing_country: portalData.billing_country || '',
        shipping_address_line1: portalData.shipping_address_line1 || '',
        shipping_address_line2: portalData.shipping_address_line2 || '',
        shipping_city: portalData.shipping_city || '',
        shipping_state: portalData.shipping_state || '',
        shipping_zip: portalData.shipping_zip || '',
        shipping_country: portalData.shipping_country || '',
        phone: portalData.phone || '',
      });
    }
  }, [portalData]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axiosInstance.put(`/api/finance/portal/${portalToken}/billing`, form);
      if (res.data?.success !== false) {
        setSnackbar({ open: true, message: 'Billing information updated successfully', severity: 'success' });
        setEditing(false);
        if (onPortalDataUpdate) onPortalDataUpdate({ ...portalData, ...form });
      } else {
        setSnackbar({ open: true, message: res.data?.message || 'Failed to update', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to update billing information', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const textFieldSx = {
    '& .MuiInputBase-root': { fontFamily, fontSize: '0.875rem', borderRadius: '8px' },
    '& .MuiInputLabel-root': { fontFamily, fontSize: '0.875rem' },
  };

  const renderAddressBlock = (title, prefix) => (
    <Box sx={{ mb: 3 }}>
      <Typography sx={{ fontFamily, fontSize: '0.875rem', fontWeight: 700, color: '#111827', mb: 2 }}>
        {title}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Address Line 1"
            value={form[`${prefix}_address_line1`]}
            onChange={handleChange(`${prefix}_address_line1`)}
            disabled={!editing}
            size="small"
            sx={textFieldSx}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Address Line 2"
            value={form[`${prefix}_address_line2`]}
            onChange={handleChange(`${prefix}_address_line2`)}
            disabled={!editing}
            size="small"
            sx={textFieldSx}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="City"
            value={form[`${prefix}_city`]}
            onChange={handleChange(`${prefix}_city`)}
            disabled={!editing}
            size="small"
            sx={textFieldSx}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="State"
            value={form[`${prefix}_state`]}
            onChange={handleChange(`${prefix}_state`)}
            disabled={!editing}
            size="small"
            sx={textFieldSx}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="ZIP Code"
            value={form[`${prefix}_zip`]}
            onChange={handleChange(`${prefix}_zip`)}
            disabled={!editing}
            size="small"
            sx={textFieldSx}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="Country"
            value={form[`${prefix}_country`]}
            onChange={handleChange(`${prefix}_country`)}
            disabled={!editing}
            size="small"
            sx={textFieldSx}
          />
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Box>
      <Card sx={sxCard}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography sx={{ fontFamily, fontSize: '1rem', fontWeight: 700, color: '#111827' }}>
              Billing Information
            </Typography>
            {!editing ? (
              <Button
                startIcon={<EditIcon />}
                onClick={() => setEditing(true)}
                sx={{
                  fontFamily,
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  textTransform: 'none',
                  color: ACCENT,
                  '&:hover': { backgroundColor: '#f0fdf4' },
                }}
              >
                Edit
              </Button>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  onClick={() => setEditing(false)}
                  sx={{
                    fontFamily,
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                    textTransform: 'none',
                    color: '#6b7280',
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving}
                  sx={{
                    fontFamily,
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                    textTransform: 'none',
                    backgroundColor: ACCENT,
                    borderRadius: '8px',
                    boxShadow: 'none',
                    '&:hover': { backgroundColor: '#16a34a', boxShadow: 'none' },
                  }}
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </Box>
            )}
          </Box>

          {renderAddressBlock('Billing Address', 'billing')}

          <Divider sx={{ my: 3 }} />

          {renderAddressBlock('Shipping Address', 'shipping')}

          <Divider sx={{ my: 3 }} />

          <Typography sx={{ fontFamily, fontSize: '0.875rem', fontWeight: 700, color: '#111827', mb: 2 }}>
            Contact
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={form.phone}
                onChange={handleChange('phone')}
                disabled={!editing}
                size="small"
                sx={textFieldSx}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ fontFamily }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ─────────────────────────── DISPUTES TAB ───────────────────────────
function DisputesTab({ portalToken }) {
  const [disputes, setDisputes] = useState([]);
  const [unpaidInvoices, setUnpaidInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [disputeForm, setDisputeForm] = useState({ invoice_id: '', reason: '' });

  const fetchDisputes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/api/finance/portal/${portalToken}/disputes`);
      setDisputes(res.data?.data?.disputes || res.data?.disputes || []);
    } catch (err) {
      console.error('Failed to fetch disputes', err);
    } finally {
      setLoading(false);
    }
  }, [portalToken]);

  const fetchUnpaidInvoices = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/api/finance/portal/${portalToken}/invoices?status=SENT&per_page=100`);
      const sent = res.data?.data?.invoices || res.data?.invoices || [];
      const res2 = await axiosInstance.get(`/api/finance/portal/${portalToken}/invoices?status=OVERDUE&per_page=100`);
      const overdue = res2.data?.data?.invoices || res2.data?.invoices || [];
      setUnpaidInvoices([...sent, ...overdue]);
    } catch (err) {
      console.error('Failed to fetch unpaid invoices', err);
    }
  }, [portalToken]);

  useEffect(() => {
    fetchDisputes();
    fetchUnpaidInvoices();
  }, [fetchDisputes, fetchUnpaidInvoices]);

  const handleOpenDialog = () => {
    setDisputeForm({ invoice_id: '', reason: '' });
    setDialogOpen(true);
  };

  const handleSubmitDispute = async () => {
    if (!disputeForm.invoice_id || !disputeForm.reason.trim()) return;
    setSubmitting(true);
    try {
      const res = await axiosInstance.post(`/api/finance/portal/${portalToken}/disputes`, disputeForm);
      if (res.data?.success !== false) {
        setSnackbar({ open: true, message: 'Dispute filed successfully', severity: 'success' });
        setDialogOpen(false);
        fetchDisputes();
      } else {
        setSnackbar({ open: true, message: res.data?.message || 'Failed to file dispute', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to file dispute', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress sx={{ color: ACCENT }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
          sx={{
            fontFamily,
            fontWeight: 600,
            fontSize: '0.8125rem',
            textTransform: 'none',
            backgroundColor: ACCENT,
            borderRadius: '8px',
            boxShadow: 'none',
            '&:hover': { backgroundColor: '#16a34a', boxShadow: 'none' },
          }}
        >
          File Dispute
        </Button>
      </Box>

      <TableContainer component={Paper} sx={sxCard}>
        <Table>
          <TableHead sx={sxTableHead}>
            <TableRow>
              <TableCell>Dispute #</TableCell>
              <TableCell>Invoice #</TableCell>
              <TableCell>Date Filed</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {disputes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} sx={{ ...sxTableCell, textAlign: 'center', color: '#9ca3af', py: 4 }}>
                  No disputes found
                </TableCell>
              </TableRow>
            ) : (
              disputes.map((d) => (
                <TableRow key={d.id} sx={{ '&:hover': { backgroundColor: '#f9fafb' } }}>
                  <TableCell sx={{ ...sxTableCell, fontWeight: 600 }}>{d.dispute_number || d.id}</TableCell>
                  <TableCell sx={sxTableCell}>{d.invoice_number || d.invoice_id || '-'}</TableCell>
                  <TableCell sx={sxTableCell}>{formatDate(d.created_at)}</TableCell>
                  <TableCell sx={{ ...sxTableCell, maxWidth: 300 }}>
                    <Typography
                      sx={{
                        fontFamily,
                        fontSize: '0.875rem',
                        color: '#374151',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {d.reason}
                    </Typography>
                  </TableCell>
                  <TableCell sx={sxTableCell}>
                    <StatusChip status={d.status} colorMap={disputeStatusColors} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', fontFamily } }}
      >
        <DialogTitle sx={{ fontFamily, fontWeight: 700, fontSize: '1.125rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          File a Dispute
          <IconButton onClick={() => setDialogOpen(false)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel sx={{ fontFamily, fontSize: '0.875rem' }}>Select Invoice</InputLabel>
            <Select
              value={disputeForm.invoice_id}
              label="Select Invoice"
              onChange={(e) => setDisputeForm((prev) => ({ ...prev, invoice_id: e.target.value }))}
              sx={{ fontFamily, fontSize: '0.875rem', borderRadius: '8px' }}
            >
              {unpaidInvoices.map((inv) => (
                <MenuItem key={inv.id} value={inv.id} sx={{ fontFamily, fontSize: '0.875rem' }}>
                  {inv.invoice_number || inv.id} - {formatCurrency(inv.balance_due)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Reason for Dispute"
            value={disputeForm.reason}
            onChange={(e) => setDisputeForm((prev) => ({ ...prev, reason: e.target.value }))}
            size="small"
            sx={{
              '& .MuiInputBase-root': { fontFamily, fontSize: '0.875rem', borderRadius: '8px' },
              '& .MuiInputLabel-root': { fontFamily, fontSize: '0.875rem' },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            sx={{ fontFamily, fontWeight: 600, fontSize: '0.8125rem', textTransform: 'none', color: '#6b7280' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitDispute}
            disabled={submitting || !disputeForm.invoice_id || !disputeForm.reason.trim()}
            sx={{
              fontFamily,
              fontWeight: 600,
              fontSize: '0.8125rem',
              textTransform: 'none',
              backgroundColor: ACCENT,
              borderRadius: '8px',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#16a34a', boxShadow: 'none' },
            }}
          >
            {submitting ? 'Submitting...' : 'Submit Dispute'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ fontFamily }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ─────────────────────────── MAIN COMPONENT ───────────────────────────
const tabConfig = [
  { label: 'Overview', icon: <DashboardIcon fontSize="small" /> },
  { label: 'Invoices', icon: <ReceiptIcon fontSize="small" /> },
  { label: 'Payments', icon: <PaymentIcon fontSize="small" /> },
  { label: 'Statements', icon: <DescriptionIcon fontSize="small" /> },
  { label: 'Billing Info', icon: <LocationIcon fontSize="small" /> },
  { label: 'Disputes', icon: <DisputeIcon fontSize="small" /> },
];

export default function FinanceCustomerPortal() {
  const { portalToken } = useParams();
  const [portalData, setPortalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const fetchPortal = async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.get(`/api/finance/portal/${portalToken}`);
        setPortalData(res.data?.data || res.data);
      } catch (err) {
        if (err.response?.status === 404) {
          setError('Invalid or expired portal link');
        } else {
          setError(err.response?.data?.message || 'Failed to load portal');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchPortal();
  }, [portalToken]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f9fafb',
        }}
      >
        <CircularProgress sx={{ color: ACCENT }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f9fafb',
        }}
      >
        <Card sx={{ ...sxCard, maxWidth: 480, width: '100%', mx: 2 }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: '#fee2e2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <DisputeIcon sx={{ fontSize: 32, color: '#dc2626' }} />
            </Box>
            <Typography sx={{ fontFamily, fontSize: '1.25rem', fontWeight: 700, color: '#111827', mb: 1 }}>
              Portal Unavailable
            </Typography>
            <Typography sx={{ fontFamily, fontSize: '0.875rem', color: '#6b7280' }}>
              {error}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return <OverviewTab portalData={portalData} portalToken={portalToken} />;
      case 1:
        return <InvoicesTab portalToken={portalToken} />;
      case 2:
        return <PaymentsTab portalToken={portalToken} />;
      case 3:
        return <StatementsTab portalToken={portalToken} />;
      case 4:
        return (
          <BillingInfoTab
            portalToken={portalToken}
            portalData={portalData}
            onPortalDataUpdate={setPortalData}
          />
        );
      case 5:
        return <DisputesTab portalToken={portalToken} />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      {/* Green accent header bar */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${ACCENT} 0%, #16a34a 100%)`,
          py: 4,
          px: 3,
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Typography sx={{ fontFamily, fontSize: '0.75rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)', textTransform: 'uppercase', letterSpacing: '0.1em', mb: 0.5 }}>
            Customer Portal
          </Typography>
          <Typography sx={{ fontFamily, fontSize: '1.5rem', fontWeight: 700, color: '#fff' }}>
            {portalData.display_name || portalData.customer_name || 'Welcome'}
          </Typography>
        </Box>
      </Box>

      {/* Main content */}
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: 3, py: 4 }}>
        {/* Tabs */}
        <Box
          sx={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            mb: 3,
            overflow: 'hidden',
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, newVal) => setActiveTab(newVal)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 48,
              '& .MuiTab-root': {
                fontFamily,
                fontWeight: 600,
                fontSize: '0.8125rem',
                textTransform: 'none',
                minHeight: 48,
                color: '#6b7280',
                gap: '6px',
              },
              '& .Mui-selected': {
                color: `${ACCENT} !important`,
              },
              '& .MuiTabs-indicator': {
                backgroundColor: ACCENT,
                height: 3,
                borderRadius: '3px 3px 0 0',
              },
            }}
          >
            {tabConfig.map((tab, idx) => (
              <Tab key={idx} label={tab.label} icon={tab.icon} iconPosition="start" />
            ))}
          </Tabs>
        </Box>

        {/* Tab content */}
        {renderTabContent()}
      </Box>
    </Box>
  );
}
